import React from "react";
import * as yup from "yup";
import i18n, { useTranslation } from "utilities/i18n";
import { FeatureFlagEnabled, LoadingIndicator } from "elements";
import {
  DISMISS,
  Formik,
  FormikHelpers,
  Input,
  InputProps,
  useNotification
} from "collections";
import { Form } from "collections/Form/Form";
import BlockingForm from "collections/Form/BlockingForm";
import {
  DeviceFragment,
  useNormalizedDeviceSettingsQuery,
  useUpsertNormalizedDeviceSettingsMutation
} from "generated/graphql";
import { generatePath, Route, Switch, useRouteMatch } from "polyrouter";
import { Layout } from "views/Main/Layouts";
import { DeviceHeader } from "../DeviceHeader";
import "./RelayDeviceSettings.scss";
import { toNumber } from "utilities/numberUtils";
import { PasswordInput } from "collections/Form/PasswordInput";
import { ValueOf } from "utilities/types";

export enum SETTINGS {
  USERNAME = "username",
  PASSWORD = "password",
  RPRM_HOST = "rprmhost",
  INVENTORY_FREQ = "inventorypollfreq",
  STATUS_FREQ = "statuspollfreq",
  CDR_FREQ = "cdrpollfreq"
}

export const SETTINGS_PREFIX = "edgeRPRMConnector";

const getTranslation = (name: ValueOf<SETTINGS>) => {
  switch (name) {
    case SETTINGS.USERNAME:
      return i18n.t("USERNAME");
    case SETTINGS.PASSWORD:
      return i18n.t("PASSWORD");
    case SETTINGS.RPRM_HOST:
      return i18n.t("RPRM_HOST");
    case SETTINGS.INVENTORY_FREQ:
      return i18n.t("RPRM_INVENTORY_POLL_FREQUENCY");
    case SETTINGS.STATUS_FREQ:
      return i18n.t("RPRM_STATUS_POLL_FREQUENCY");
    case SETTINGS.CDR_FREQ:
      return i18n.t("RPRM_CDR_POLL_FREQUENCY");
    default:
      console.error("getTranslation missing key", name);
      return "";
  }
};

const RelaySettingsFormSchema = yup.object({
  [SETTINGS.USERNAME]: yup
    .string()
    .label(getTranslation(SETTINGS.USERNAME))
    .required(),
  [SETTINGS.PASSWORD]: yup
    .string()
    .label(getTranslation(SETTINGS.PASSWORD))
    .required(),
  [SETTINGS.RPRM_HOST]: yup
    .string()
    .label(getTranslation(SETTINGS.RPRM_HOST))
    .url()
    .required(),
  [SETTINGS.INVENTORY_FREQ]: yup
    .number()
    .positive(i18n.t("POLL_INTERVAL_ERROR")),
  [SETTINGS.STATUS_FREQ]: yup.number().positive(i18n.t("POLL_INTERVAL_ERROR")),
  [SETTINGS.CDR_FREQ]: yup.number().positive(i18n.t("POLL_INTERVAL_ERROR"))
});

type RelaySettingsForm = yup.InferType<typeof RelaySettingsFormSchema>;

const FormInput: React.FC<InputProps> = ({ name, type = "text" }) => {
  const { t } = useTranslation();
  const isNumberField = type === "number";

  return (
    <div className="form-group">
      <label className="col-4 text-right" htmlFor={name + "-input"}>
        {getTranslation(name)}:
      </label>
      <div className={isNumberField ? "col-1" : "col-4"}>
        <Input name={name} data-testid={name} type={type} />
      </div>
      {isNumberField && <span className="col-1">{t("SECONDS")}</span>}
    </div>
  );
};

const AccountSettings: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <div className="card title-card">{t("RPRM_ACCOUNT")}</div>
      <div className="details grid form">
        <FormInput name={SETTINGS.USERNAME} type="text" />
        <PasswordInput
          name={SETTINGS.PASSWORD}
          label={getTranslation(SETTINGS.PASSWORD)}
        />
      </div>
    </>
  );
};

const ServerSettings = () => {
  const { t } = useTranslation();

  return (
    <>
      <div className="card title-card">{t("RPRM_SERVER")}</div>
      <div className="details grid form">
        <FormInput name={SETTINGS.RPRM_HOST} type="text" />
        <FormInput name={SETTINGS.INVENTORY_FREQ} type="number" />
        <FormInput name={SETTINGS.STATUS_FREQ} type="number" />
        <FormInput name={SETTINGS.CDR_FREQ} type="number" />
      </div>
    </>
  );
};

export const RelaySettings: React.FC<{
  device: DeviceFragment;
}> = ({ device }) => {
  const { t } = useTranslation();
  const [addNotification] = useNotification();
  const {
    path,
    params: { deviceId }
  } = useRouteMatch<{ category: string; deviceId: string }>();

  const [, upsertSettings] = useUpsertNormalizedDeviceSettingsMutation();
  const {
    data,
    error,
    fetching,
    executor: refresh
  } = useNormalizedDeviceSettingsQuery({
    variables: { id: device.id }
  });

  if (fetching) return <LoadingIndicator className="mt-3" />;
  if (error || !data) return null;

  const currentSettings = data.normalizedDeviceSettings.settings
    .filter(({ name }) => name.split(".")[0] === SETTINGS_PREFIX)
    .map(({ name, value }) => ({ name: name.split(".")[1], value }))
    .reduce(
      (acc, { name, value }) => ({ ...acc, [name]: value ?? "" }),
      {} as Record<SETTINGS, string>
    );

  const initialValues: RelaySettingsForm = {
    [SETTINGS.USERNAME]: currentSettings[SETTINGS.USERNAME],
    [SETTINGS.PASSWORD]: currentSettings[SETTINGS.PASSWORD],
    [SETTINGS.RPRM_HOST]: currentSettings[SETTINGS.RPRM_HOST],
    [SETTINGS.INVENTORY_FREQ]: toNumber(
      currentSettings[SETTINGS.INVENTORY_FREQ]
    ),
    [SETTINGS.STATUS_FREQ]: toNumber(currentSettings[SETTINGS.STATUS_FREQ]),
    [SETTINGS.CDR_FREQ]: toNumber(currentSettings[SETTINGS.CDR_FREQ])
  };

  const submit = async (
    values: RelaySettingsForm,
    helpers: FormikHelpers<RelaySettingsForm>
  ) => {
    const settingsInput = Object.entries(values).map(([key, value]) => ({
      name: `${SETTINGS_PREFIX}.${key}`,
      value: value.toString()
    }));

    const { error } = await upsertSettings(
      {
        settings: settingsInput,
        id: device.id
      },
      { notify: { i18nKey: "ERROR_SAVING_DEVICE_SETTINGS" } }
    );

    if (!error) {
      addNotification({
        type: "success",
        message: t("DEVICE_SETTINGS_UPDATED"),
        dismiss: DISMISS.DEFAULT
      });
    }

    refresh({ requestPolicy: "network-only" });
    helpers.setSubmitting(false);
  };

  return (
    <Formik
      name="RelaySettings"
      initialValues={initialValues}
      validationSchema={RelaySettingsFormSchema}
      enableReinitialize={true}
      onSubmit={submit}
    >
      {({ dirty }) => (
        <BlockingForm unsavedChanges={dirty} allowedSubRoutes={["/settings"]}>
          <Form className="form pb-2 relay-settings">
            <DeviceHeader
              device={device}
              pageTitle={
                <>
                  {`${device?.hardwareModel} |`}
                  <p className={"d-inline-block device-header-name"}>
                    {device?.name || device?.room?.name || device?.macAddress}
                  </p>
                </>
              }
              hasActionButtons
            />
            <Layout.Content.ScrollingList>
              <Switch>
                <Route
                  path={generatePath(path, { deviceId, category: "server" })}
                  component={ServerSettings}
                />
                <Route>
                  <FeatureFlagEnabled feature="RELAY_RPRM">
                    <AccountSettings />
                  </FeatureFlagEnabled>
                </Route>
              </Switch>
            </Layout.Content.ScrollingList>
          </Form>
        </BlockingForm>
      )}
    </Formik>
  );
};

export default RelaySettings;
